import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { BACKEND_URL } from "@/lib/config";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, SectionCard } from "@/components/ui-bits";
import { ArrowLeft, Save, Trash2, ChevronLeft, ChevronRight, Upload, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

// Use pdfjs worker via CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PLACEHOLDERS = [
  { key: "invoice_number", label: "Invoice #" },
  { key: "doc_type", label: "Doc type" },
  { key: "issue_date", label: "Issue date" },
  { key: "due_date", label: "Due date" },
  { key: "client_name", label: "Client name" },
  { key: "client_address", label: "Client address" },
  { key: "client_gstin", label: "Client GSTIN" },
  { key: "client_email", label: "Client email" },
  { key: "client_phone", label: "Client phone" },
  { key: "brand_name", label: "Brand name" },
  { key: "brand_address", label: "Brand address" },
  { key: "brand_gstin", label: "Brand GSTIN" },
  { key: "bank_details", label: "Bank details" },
  { key: "subtotal", label: "Subtotal" },
  { key: "gst_total", label: "GST total" },
  { key: "total", label: "Total" },
  { key: "amount", label: "Amount" },
  { key: "paid_amount", label: "Paid amount" },
  { key: "balance_due", label: "Balance due" },
  { key: "notes", label: "Notes" },
];

const SPECIAL_PLACEHOLDERS = [
  { key: "brand_logo", label: "Brand logo (box)", kind: "brand_logo" },
];

const ALIGNS = ["left", "center", "right"];

export default function TemplateEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [template, setTemplate] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [fields, setFields] = useState([]);
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState({});
  const [scale, setScale] = useState(1.1);
  const containerRef = useRef(null);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });

  const load = useCallback(async () => {
    const r = await api.get(`/templates/${id}`);
    setTemplate(r.data);
    // Assign a stable unique UI id to each field (system templates ship without one).
    // We strip _uid back out on save.
    let counter = 1;
    const withIds = (r.data.fields || []).map((f) => ({ ...f, _uid: f._uid || `f_${counter++}` }));
    setFields(withIds);
    api.get(`/templates/${id}/preview-data`).then((p) => setPreview(p.data));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!template) return <div className="text-muted-foreground text-sm">Loading…</div>;

  const fileUrl = `${BACKEND_URL}${template.file_url}`;

  const onDocLoad = (pdf) => setNumPages(pdf.numPages);
  const onPageLoad = (page) => {
    const viewport = page.getViewport({ scale });
    setPageSize({ width: viewport.width, height: viewport.height });
  };

  const onDropPlaceholder = (e) => {
    e.preventDefault();
    const key = e.dataTransfer.getData("placeholder");
    const kind = e.dataTransfer.getData("kind") || "text";
    if (!key) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = (e.clientX - rect.left) / rect.width;
    const ny = (e.clientY - rect.top) / rect.height;
    const base = {
      _uid: `f_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      kind,
      key,
      page: pageNum - 1,
      x: Math.max(0, Math.min(1, nx)),
      y: Math.max(0, Math.min(1, ny)),
    };
    const field = kind === "brand_logo"
      ? { ...base, width: 0.20, height: 0.085 }
      : { ...base, font_size: 10, bold: false, align: "left" };
    setFields((arr) => [...arr, field]);
    setSelected(field._uid);
  };

  const moveField = (uid, nx, ny) => {
    setFields((arr) => arr.map((f) => (f._uid === uid ? { ...f, x: nx, y: ny } : f)));
  };

  const onFieldMouseDown = (e, field) => {
    e.stopPropagation();
    setSelected(field._uid);
    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const onMove = (ev) => {
      const nx = (ev.clientX - rect.left) / rect.width;
      const ny = (ev.clientY - rect.top) / rect.height;
      moveField(field._uid, Math.max(0, Math.min(1, nx)), Math.max(0, Math.min(1, ny)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const removeField = (uid) => setFields((arr) => arr.filter((f) => f._uid !== uid));
  const updateField = (uid, patch) => setFields((arr) => arr.map((f) => (f._uid === uid ? { ...f, ...patch } : f)));

  const save = async () => {
    try {
      const payload = fields.map(({ _uid, ...rest }) => rest);
      await api.put(`/templates/${id}/fields`, { fields: payload });
      toast.success("Mapping saved");
      load();
    } catch (e) { toast.error("Save failed"); }
  };

  const replacePdf = async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) { toast.error("PDF only"); return; }
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/templates/${id}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF replaced — mappings preserved");
      // Force PDF.js to refresh by reloading the template (file_url stays the same)
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Replace failed"); }
  };

  const fieldsOnPage = fields.filter((f) => f.page === pageNum - 1);
  const sel = fields.find((f) => f._uid === selected);

  return (
    <div data-testid="template-editor-page">
      <button onClick={() => nav("/templates")} className="text-xs text-muted-foreground hover:text-bronze flex items-center gap-1 mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to templates
      </button>
      <PageHeader
        eyebrow="PDF Engine"
        title={template.name}
        description="Drag chips onto the PDF. Click a placed field to edit. Drag to move. Use 'Replace PDF' to swap the underlying template (mappings preserved)."
        actions={
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-1.5 px-3 h-9 rounded-md border border-border bg-white text-sm cursor-pointer hover:border-[#c5a059]/40 hover:text-bronze" data-testid="replace-pdf-btn">
              <Upload className="w-4 h-4" /> Replace PDF
              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => replacePdf(e.target.files?.[0])} />
            </label>
            <Button onClick={save} className="bg-bronze text-black hover:bg-[#d4af68]" data-testid="save-mapping-btn"><Save className="w-4 h-4 mr-1.5" /> Save mapping</Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <SectionCard
          title={`Page ${pageNum} of ${numPages}`}
          action={
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => setPageNum((p) => Math.max(1, p - 1))} disabled={pageNum <= 1}><ChevronLeft className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => setPageNum((p) => Math.min(numPages, p + 1))} disabled={pageNum >= numPages}><ChevronRight className="w-4 h-4" /></Button>
              <select value={scale} onChange={(e) => setScale(parseFloat(e.target.value))} className="bg-white border border-border rounded text-xs px-2 py-1">
                {[0.75, 1, 1.1, 1.25, 1.5].map((s) => <option key={s} value={s}>{Math.round(s*100)}%</option>)}
              </select>
            </div>
          }
        >
          <div className="overflow-auto bg-[#f5f5f4] rounded-lg p-4 flex justify-center" ref={containerRef}>
            <div
              className="relative inline-block shadow-2xl"
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropPlaceholder}
              onClick={() => setSelected(null)}
              data-testid="pdf-drop-area"
            >
              <Document file={fileUrl} onLoadSuccess={onDocLoad} loading={<div className="p-10 text-muted-foreground text-sm">Loading PDF…</div>}>
                <Page pageNumber={pageNum} scale={scale} onLoadSuccess={onPageLoad} renderAnnotationLayer={false} renderTextLayer={false} />
              </Document>

              {/* Overlay placed fields */}
              {pageSize.width > 0 && fieldsOnPage.map((f) => {
                const isSel = selected === f._uid;

                // Brand-logo box: render as a visible rectangle with the configured width/height
                if (f.kind === "brand_logo") {
                  const left = f.x * pageSize.width;
                  const top = f.y * pageSize.height;
                  const boxW = (f.width ?? 0.20) * pageSize.width;
                  const boxH = (f.height ?? 0.085) * pageSize.height;
                  return (
                    <div
                      key={f._uid}
                      onMouseDown={(e) => onFieldMouseDown(e, f)}
                      onClick={(e) => { e.stopPropagation(); setSelected(f._uid); }}
                      className={`absolute cursor-move select-none border-2 ${isSel ? "border-[#c5a059] bg-[#c5a059]/10" : "border-[#c5a059]/60 border-dashed bg-[#c5a059]/5"} flex items-center justify-center`}
                      style={{ left, top, width: boxW, height: boxH }}
                      data-testid={`placed-field-${f.key}`}
                    >
                      <div className="text-[10px] uppercase tracking-[0.18em] text-bronze font-medium flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" /> Logo box
                      </div>
                    </div>
                  );
                }

                const left = f.x * pageSize.width;
                const top = f.y * pageSize.height;
                return (
                  <div
                    key={f._uid}
                    onMouseDown={(e) => onFieldMouseDown(e, f)}
                    onClick={(e) => { e.stopPropagation(); setSelected(f._uid); }}
                    className={`absolute -translate-y-1/2 px-1.5 py-0.5 rounded text-[11px] font-medium cursor-move select-none whitespace-nowrap ${
                      isSel
                        ? "bg-[#c5a059] text-black ring-2 ring-[#c5a059]"
                        : "bg-white/95 text-[#9a7430] border border-[#c5a059]/60"
                    }`}
                    style={{
                      left,
                      top,
                      transform: `translateY(-50%) translateX(${f.align === "right" ? "-100%" : f.align === "center" ? "-50%" : "0"})`,
                      fontSize: Math.max(9, f.font_size),
                      fontWeight: f.bold ? 700 : 500,
                    }}
                    data-testid={`placed-field-${f.key}`}
                  >
                    {String(preview[f.key] ?? `{${f.key}}`)}
                  </div>
                );
              })}
            </div>
          </div>
        </SectionCard>

        {/* Sidebar */}
        <div className="space-y-4">
          <SectionCard title="Placeholders">
            <div className="grid grid-cols-2 gap-2">
              {PLACEHOLDERS.map((p) => (
                <div
                  key={p.key}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData("placeholder", p.key); e.dataTransfer.setData("kind", "text"); }}
                  className="cursor-grab text-[11px] px-2 py-1.5 rounded border border-border bg-[#fafaf9] hover:border-[#c5a059]/40 hover:text-bronze"
                  data-testid={`placeholder-${p.key}`}
                  title={`{${p.key}}`}
                >
                  {p.label}
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2">Special</div>
              <div className="grid grid-cols-2 gap-2">
                {SPECIAL_PLACEHOLDERS.map((p) => (
                  <div
                    key={p.key}
                    draggable
                    onDragStart={(e) => { e.dataTransfer.setData("placeholder", p.key); e.dataTransfer.setData("kind", p.kind); }}
                    className="cursor-grab text-[11px] px-2 py-1.5 rounded border border-[#c5a059]/40 bg-[#c5a059]/5 text-bronze hover:bg-[#c5a059]/10 flex items-center gap-1.5"
                    data-testid={`placeholder-${p.key}`}
                    title={`{${p.key}}`}
                  >
                    <ImageIcon className="w-3 h-3" /> {p.label}
                  </div>
                ))}
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-3">Drag any chip onto the PDF →</div>
          </SectionCard>

          {sel ? (
            <SectionCard title="Field properties">
              <div className="space-y-3 text-xs">
                <div>
                  <div className="text-muted-foreground mb-1">Placeholder</div>
                  <div className="font-mono text-bronze">{`{${sel.key}}`}{sel.kind === "brand_logo" && <span className="ml-2 text-muted-foreground">· box</span>}</div>
                </div>

                {sel.kind === "brand_logo" ? (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <div className="text-muted-foreground mb-1">Width (% of page)</div>
                        <Input type="number" step="0.005" value={sel.width ?? 0.20} onChange={(e) => updateField(sel._uid, { width: Math.max(0.01, Math.min(1, Number(e.target.value) || 0.20)) })} className="bg-white h-8" data-testid="logo-width" />
                      </div>
                      <div>
                        <div className="text-muted-foreground mb-1">Height (% of page)</div>
                        <Input type="number" step="0.005" value={sel.height ?? 0.085} onChange={(e) => updateField(sel._uid, { height: Math.max(0.01, Math.min(1, Number(e.target.value) || 0.085)) })} className="bg-white h-8" data-testid="logo-height" />
                      </div>
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      Brand logos scale-to-fit inside this box and are centred. Aspect ratio is preserved for every brand's logo.
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="text-muted-foreground mb-1">Font size</div>
                      <Input type="number" value={sel.font_size} onChange={(e) => updateField(sel._uid, { font_size: Number(e.target.value || 10) })} className="bg-white h-8" />
                    </div>
                    <div>
                      <div className="text-muted-foreground mb-1">Align</div>
                      <Select value={sel.align} onValueChange={(v) => updateField(sel._uid, { align: v })}>
                        <SelectTrigger className="bg-white h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{ALIGNS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={!!sel.bold} onChange={(e) => updateField(sel._uid, { bold: e.target.checked })} /> Bold</label>
                  </>
                )}

                <div className="flex justify-between pt-2 border-t border-border">
                  <span className="text-muted-foreground font-mono">{(sel.x * 100).toFixed(1)}%, {(sel.y * 100).toFixed(1)}%</span>
                  <button onClick={() => removeField(sel._uid)} className="text-rose-400 hover:underline flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                </div>
              </div>
            </SectionCard>
          ) : (
            <SectionCard title="Tips">
              <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
                <li>Drag any chip onto the page to drop a placeholder.</li>
                <li>Drag a placed field to move it.</li>
                <li>Click a placed field to edit font / align / box size.</li>
                <li>{fields.length} field{fields.length === 1 ? "" : "s"} mapped on this template.</li>
                <li>Click <span className="text-bronze">Save mapping</span> to persist.</li>
              </ul>
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
}
